import { useEffect, useId, useRef, useState } from "react";

const WORDS = "FIVE OUT";

/**
 * Each letter's seam: one line of stitching down the middle of every stroke,
 * in font units (1/1000 em, y up from the baseline is negative), from the pen
 * position. Measured off the bundled Inter at weight 900 - stems are 47-243
 * wide, so their center is 145 - and stopped short of each stroke's end, the
 * way a seam stops short of the edge of the cloth. Another face, or another
 * word, needs measuring again; a letter missing here is simply not stitched.
 */
const SEAMS: Record<string, string> = {
  F: "M500,-648 H145 V-58 M145,-327 H468",
  I: "M145,-670 V-58",
  V: "M151,-670 L289,-220 L397,-78 L505,-220 L643,-670",
  E: "M512,-648 H145 V-80 H510 M145,-368 H485",
  O: "M639,-364 A252,287.5 0 1 0 135,-364 A252,287.5 0 1 0 639,-364 Z",
  U: "M145,-670 V-300 A215.5,223 0 0 0 576,-300 V-670",
  T: "M85,-648 H600 M343,-648 V-58",
};
/** The title is set at 100 user units to the em; the seams are drawn in 1000. */
const SEAM_SCALE = 0.1;
/** The thread's width, and one stitch and the gap to the next, in seam units. Round caps lengthen each stitch by a thread. */
const THREAD = 17;
const STITCH_DASH = "30 42";
/** Above the baseline to the top of the box, and below it: room for the caps and the cloth's shadow. */
const ASCENT = 80;
const DESCENT = 8;
/** A first guess at the word's width before it can be measured in the real face. */
const WIDTH_GUESS = 440;

/**
 * The home screen's title, every letter cut out of the table's felt and
 * stitched down the middle of every stroke in the table's thread.
 *
 * It is the venue's own cloth: `--felt`, `--felt-light`, `--felt-shade` and
 * `--felt-thread` are published from the venue's Pixi colors by `applyVenue`,
 * so the letters are the same fabric and thread as the table the player is
 * about to sit at, and change with the room.
 *
 * Drawn as one inline SVG over the app's own Inter at its heaviest (no
 * download). The seam is a single line of stitching down the middle of each
 * stroke (`SEAMS`), placed at each glyph's measured pen position, so it moves
 * with the letters whatever the screen size. All static paint: nothing animates.
 */
export function CardTitle() {
  const id = useId().replace(/:/g, "");
  const textRef = useRef<SVGTextElement | null>(null);
  const [box, setBox] = useState({ x: -4, w: WIDTH_GUESS });
  /** Each stitched letter's pen position, once the real face is in. */
  const [pens, setPens] = useState<{ c: string; x: number }[]>([]);

  // Measured in the real face once it is in: the fallback's widths are not its own.
  useEffect(() => {
    let stopped = false;
    const measure = () => {
      const text = textRef.current;
      const b = text?.getBBox();
      if (stopped || !text || !b || b.width <= 0) return;
      setBox({ x: b.x - 4, w: b.width + 8 });
      setPens(
        [...WORDS].flatMap((c, i) => (SEAMS[c] ? [{ c, x: text.getStartPositionOfChar(i).x }] : [])),
      );
    };
    measure();
    void document.fonts?.ready.then(measure);
    return () => {
      stopped = true;
    };
  }, []);

  const glyphs = (props: React.SVGProps<SVGTextElement>, ref?: React.Ref<SVGTextElement>) => (
    <text ref={ref} x={0} y={0} className="card-title-glyphs" {...props}>
      {WORDS}
    </text>
  );
  const u = (name: string) => `url(#${id}-${name})`;

  return (
    <h1 className="card-title" aria-label="Five Out">
      <svg
        className="card-title-svg"
        viewBox={`${box.x} ${-ASCENT} ${box.w} ${ASCENT + DESCENT}`}
        style={{ aspectRatio: `${box.w} / ${ASCENT + DESCENT}` }}
        aria-hidden="true"
      >
        <defs>
          {/* Lit from the top left, as the table is. */}
          <linearGradient id={`${id}-cloth`} x1="0" y1="0" x2="0.35" y2="1">
            <stop offset="0" style={{ stopColor: "var(--felt-light)" }} />
            <stop offset="0.55" style={{ stopColor: "var(--felt)" }} />
            <stop offset="1" style={{ stopColor: "var(--felt-shade)" }} />
          </linearGradient>
          {/*
            Felt: a fuzz of darker and lighter fibres over the cloth, and the
            padding of a cut piece of fabric - its lower right edge turning
            away into shade, its upper left catching the light.
          */}
          <filter id={`${id}-felt`} x="-5%" y="-10%" width="110%" height="125%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="1.3" numOctaves="3" seed="7" />
            <feGaussianBlur stdDeviation="0.35" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  -0.9 0 0 0 0.4" result="dark" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0.8 0 0 0 -0.4" result="light" />
            <feComposite in="dark" in2="SourceAlpha" operator="in" result="darkIn" />
            <feComposite in="light" in2="SourceAlpha" operator="in" result="lightIn" />
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.6" result="soft" />
            <feOffset in="soft" dx="-1.4" dy="-2" result="up" />
            <feComposite in="SourceAlpha" in2="up" operator="out" result="lowerEdge" />
            <feFlood floodColor="#000" floodOpacity="0.38" />
            <feComposite in2="lowerEdge" operator="in" result="shade" />
            <feOffset in="soft" dx="1" dy="1.4" result="down" />
            <feComposite in="SourceAlpha" in2="down" operator="out" result="upperEdge" />
            <feFlood floodColor="#fff" floodOpacity="0.22" />
            <feComposite in2="upperEdge" operator="in" result="sheen" />
            <feMerge>
              <feMergeNode in="SourceGraphic" />
              <feMergeNode in="darkIn" />
              <feMergeNode in="lightIn" />
              <feMergeNode in="shade" />
              <feMergeNode in="sheen" />
            </feMerge>
          </filter>
        </defs>

        {glyphs({ fill: u("cloth"), filter: u("felt") }, textRef)}

        {/* The seam: the thread's shadow on the cloth, the thread, and the light along its top. */}
        {pens.map(({ c, x }, i) => (
          <g key={i} transform={`translate(${x} 0) scale(${SEAM_SCALE})`} fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={STITCH_DASH}>
            <path d={SEAMS[c]} transform="translate(7 10)" stroke="#000" strokeOpacity="0.3" strokeWidth={THREAD} />
            <path d={SEAMS[c]} style={{ stroke: "var(--felt-thread)" }} strokeOpacity="0.95" strokeWidth={THREAD} />
            <path d={SEAMS[c]} transform="translate(-3 -4)" stroke="#fff" strokeOpacity="0.3" strokeWidth={THREAD * 0.45} />
          </g>
        ))}
      </svg>
    </h1>
  );
}
